if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      heroPillFrameSyncCleanup = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.variantDetailsTransitionsCleanup = this.initializeVariantDetailsTransitions();
        this.mobileMediaShowcaseCleanup = this.initializeMobileMediaShowcase() || this.initializeMobileProductGallerySliderHint();
        this.mobileScrollPillCleanup = this.initializeMobileScrollPill();
        this.heroPillFrameSyncCleanup = this.initializeHeroPillFrameSync();
        this.mountPropertyCleanup = this.initializeMountPropertySelector();
        this.artworkBwMediaCleanup = this.initializeArtworkBwMedia();
        this.mobileOptionsSelectionLocked = this.shouldLockAddToCartOnMobile();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.enforceMobileVariantDetailsExpanded();
        this.applyMobileAddToCartLock();
        this.updateAddToCartButtonLabelWithPrice();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      scrollToMobileVariantTarget() {
        const target =
          this.querySelector('.product-variant-details') ||
          this.querySelector('variant-selects') ||
          this.querySelector('.product__info-container');
        if (!target) return;

        const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
        const targetTop = target.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
        const top = Math.max(0, targetTop - headerHeight - 8);

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.variantDetailsTransitionsCleanup?.();
        this.variantDetailsTransitionsCleanup = undefined;
        this.mobileMediaShowcaseCleanup?.();
        this.mobileMediaShowcaseCleanup = undefined;
        this.mobileScrollPillCleanup?.();
        this.mobileScrollPillCleanup = undefined;
        this.heroPillFrameSyncCleanup?.();
        this.heroPillFrameSyncCleanup = undefined;
        this.mountPropertyCleanup?.();
        this.mountPropertyCleanup = undefined;
        this.artworkBwMediaCleanup?.();
        this.artworkBwMediaCleanup = undefined;
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeArtworkBwMedia() {
        const variantSelects = this.querySelector('variant-selects[data-enable-artwork-bw-media]');
        if (!variantSelects) return undefined;

        const bwMarker = (variantSelects.dataset.artworkBwMarker || '').trim().toLowerCase();
        if (!bwMarker) return undefined;

        const showcase = this.querySelector('.product-media-showcase');
        const gallery = this.querySelector('media-gallery');

        const setBw = (on) => {
          showcase?.classList.toggle('product-media-showcase--bw-preview', Boolean(on));
          gallery?.classList.toggle('media-gallery--bw-preview', Boolean(on));
        };

        const mode = (variantSelects.dataset.artworkBwMode || '').trim();

        if (mode === 'fallback') {
          const fieldset = variantSelects.querySelector('fieldset[data-artwork-bw-fallback]');
          const selectedSpan = fieldset?.querySelector('[data-selected-value]');

          const syncFallback = () => {
            const input = fieldset?.querySelector('input[data-artwork-bw-fallback-input]:checked');
            const val = (input?.value || '').toLowerCase();
            const on = bwMarker.length > 0 && val.includes(bwMarker);
            setBw(on);
            if (selectedSpan && input) selectedSpan.textContent = input.value;
          };

          syncFallback();

          const onChange = (event) => {
            if (!event.target?.matches?.('input[data-artwork-bw-fallback-input]')) return;
            syncFallback();
          };
          variantSelects.addEventListener('change', onChange);
          return () => variantSelects.removeEventListener('change', onChange);
        }

        const optName = (variantSelects.dataset.artworkOptionName || '').trim().toLowerCase();
        if (!optName) return undefined;

        let optionsMeta = [];
        try {
          const script = variantSelects.querySelector('script[data-artwork-option-position]');
          const raw = script?.textContent?.trim();
          if (raw) optionsMeta = JSON.parse(raw);
        } catch (e) {
          optionsMeta = [];
        }
        const colorSynonyms = ['color', 'colour'];
        const optIsColorSynonym = colorSynonyms.includes(optName);
        const matched = optionsMeta.find((o) => {
          const on = (o?.name || '').trim().toLowerCase();
          if (on === optName) return true;
          return optIsColorSynonym && colorSynonyms.includes(on);
        });
        const position = matched?.position;
        if (!position || position < 1 || position > 3) return undefined;

        const sync = (variant) => {
          const raw = variant?.[`option${position}`];
          const val = typeof raw === 'string' ? raw.toLowerCase() : '';
          const on = bwMarker.length > 0 && val.includes(bwMarker);
          setBw(on);
        };

        sync(this.getSelectedVariant(this));

        const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { sectionId, variant } = event.data || {};
          if (!variant || String(sectionId) !== String(this.sectionId)) return;
          sync(variant);
        });

        return () => {
          if (typeof unsub === 'function') unsub();
        };
      }

      initializeMountPropertySelector() {
        const mountInputs = Array.from(this.querySelectorAll('input[data-mount-option-input]'));
        if (!mountInputs.length) return undefined;

        const mountFieldset = mountInputs[0].closest('.product-form__input--mount-toggle');
        const mountSelectedValue = mountFieldset?.querySelector('[data-selected-value]');
        if (!mountFieldset || !mountSelectedValue) return undefined;

        const syncMountLabel = () => {
          const checked = mountFieldset.querySelector('input[data-mount-option-input]:checked');
          mountSelectedValue.textContent = checked?.value || '';
        };

        const onMountChange = (event) => {
          if (!event.target?.matches?.('input[data-mount-option-input]')) return;
          syncMountLabel();
        };

        mountFieldset.addEventListener('change', onMountChange);
        syncMountLabel();

        return () => {
          mountFieldset.removeEventListener('change', onMountChange);
        };
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initializeVariantDetailsTransitions() {
        const variantDetails = this.querySelector('.product-variant-details');
        if (!variantDetails) return undefined;

        const detailsContent = variantDetails.querySelector('.product-variant-details__content');
        if (!detailsContent) return undefined;

        const setContentHeight = (isOpen) => {
          if (isOpen) {
            detailsContent.style.setProperty('--variant-details-content-height', `${detailsContent.scrollHeight}px`);
          } else {
            detailsContent.style.setProperty('--variant-details-content-height', '0px');
          }
        };

        const measureAfterLayout = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setContentHeight(true);
            });
          });
        };

        if (variantDetails.open) {
          setContentHeight(true);
          measureAfterLayout();
        } else {
          setContentHeight(false);
        }

        const onToggle = () => {
          if (variantDetails.open) {
            setContentHeight(true);
            measureAfterLayout();
          } else {
            setContentHeight(false);
          }
        };

        variantDetails.addEventListener('toggle', onToggle);

        const onResize = () => {
          if (variantDetails.open) measureAfterLayout();
        };
        window.addEventListener('resize', onResize);

        return () => {
          variantDetails.removeEventListener('toggle', onToggle);
          window.removeEventListener('resize', onResize);
        };
      }

      initializeMobileMediaShowcase() {
        const showcase = this.querySelector('.product-media-showcase--has-hero-slide');
        if (!showcase) return undefined;

        const track = showcase.querySelector('.product-media-showcase__track');
        const counter = showcase.querySelector('[data-showcase-counter]');
        const pages = Array.from(showcase.querySelectorAll('.product-media-showcase__page'));
        const hoverCursor = showcase.querySelector('.product-media-showcase__hover-cursor');
        if (!track || !counter || pages.length < 2) return undefined;

        const getCurrentIndex = () => {
          const width = track.clientWidth || 1;
          const rawIndex = Math.round(track.scrollLeft / width);
          return Math.max(0, Math.min(pages.length - 1, rawIndex));
        };

        const setActiveIndex = (index) => {
          counter.textContent = `${index + 1} / ${pages.length}`;
          pages.forEach((page, pageIndex) => {
            page.classList.toggle('is-active', pageIndex === index);
          });
        };

        let isSwipeHinting = false;
        const syncFromScroll = () => {
          if (isSwipeHinting) return;
          setActiveIndex(getCurrentIndex());
        };

        const mobileShowcaseQuery = window.matchMedia('(max-width: 749px)');
        const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        /* v2: v1 was marking "seen" on any touch/mousedown, so the nudge never ran. */
        const SWIPE_HINT_KEY = 'hos_pdp_product_swipe_v2';
        const hintState = { timeout: null, raf: null };

        const markSwipeHintUsed = () => {
          try {
            sessionStorage.setItem(SWIPE_HINT_KEY, '1');
          } catch (e) {
            // ignore
          }
        };

        const clearSwipeHintSchedulers = () => {
          if (hintState.timeout) {
            clearTimeout(hintState.timeout);
            hintState.timeout = null;
          }
          if (hintState.raf) {
            cancelAnimationFrame(hintState.raf);
            hintState.raf = null;
          }
        };

        const endSwipeHint = (options = {}) => {
          const { resetToStart = true, remember = true } = options;
          isSwipeHinting = false;
          showcase.classList.remove('is-swipe-hint');
          track.classList.remove('swipe-hint--active-track');
          clearSwipeHintSchedulers();
          if (resetToStart) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          }
          syncFromScroll();
          if (remember) markSwipeHintUsed();
        };

        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        const runSwipeHintPeek = () => {
          if (reduceMotionQuery.matches || !mobileShowcaseQuery.matches) return;
          try {
            if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
          } catch (e) {
            // ignore
          }
          if (getCurrentIndex() !== 0 || track.scrollLeft > 2) return;

          const w = track.clientWidth || 1;
          const maxPeek = Math.min(44, w * 0.11, w * 0.42);
          const tOut = 400;
          const tHold = 120;
          const tIn = 420;
          const tTotal = tOut + tHold + tIn;
          const t0 = performance.now();

          isSwipeHinting = true;
          showcase.classList.add('is-swipe-hint');
          track.classList.add('swipe-hint--active-track');

          const step = (now) => {
            if (!isSwipeHinting) return;
            if (!mobileShowcaseQuery.matches) {
              endSwipeHint({ remember: false });
              return;
            }
            const elapsed = now - t0;
            if (elapsed >= tTotal) {
              endSwipeHint({ resetToStart: true, remember: true });
              return;
            }
            let offset = 0;
            if (elapsed < tOut) {
              const u = easeInOutCubic(elapsed / tOut);
              offset = maxPeek * u;
            } else if (elapsed < tOut + tHold) {
              offset = maxPeek;
            } else {
              const u = (elapsed - tOut - tHold) / tIn;
              const v = 1 - easeInOutCubic(u);
              offset = maxPeek * v;
            }
            track.scrollLeft = offset;
            hintState.raf = requestAnimationFrame(step);
          };
          hintState.raf = requestAnimationFrame(step);
        };

        const onTrackUserInterruptHint = () => {
          cancelScheduledShowcaseSnapToArtwork();
          if (isSwipeHinting) {
            endSwipeHint({ resetToStart: true, remember: true });
            return;
          }
          clearSwipeHintSchedulers();
        };

        const scheduleSwipeHint = () => {
          if (reduceMotionQuery.matches) return;
          try {
            if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
          } catch (e) {
            // ignore
          }
          clearSwipeHintSchedulers();
          const startAfterLayout = () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (!mobileShowcaseQuery.matches) return;
                try {
                  if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
                } catch (e) {
                  // ignore
                }
                hintState.timeout = setTimeout(() => {
                  hintState.timeout = null;
                  runSwipeHintPeek();
                }, 1000);
              });
            });
          };
          startAfterLayout();
        };
        if (mobileShowcaseQuery.matches) {
          clearSwipeHintSchedulers();
        }

        let scrollTicking = false;
        const onScroll = () => {
          if (scrollTicking) return;
          scrollTicking = true;
          requestAnimationFrame(() => {
            scrollTicking = false;
            if (!isSwipeHinting && track.scrollLeft > 6) {
              markSwipeHintUsed();
              clearSwipeHintSchedulers();
            }
            syncFromScroll();

            clearTimeout(heroSettleIdleTimer);
            heroSettleIdleTimer = window.setTimeout(() => {
              heroSettleIdleTimer = null;
              if (isSwipeHinting) return;
              if (performance.now() < suppressHeroSettleUntil) return;
              const w = track.clientWidth || 1;
              if (pages.length < 2 || w < 16) return;
              if (getCurrentIndex() !== 0) return;
              if (track.scrollLeft > w * 0.12) return;
              lastUserSettledHeroAt = performance.now();
            }, 110);
          });
        };

        let draggedDistance = 0;

        let isMouseDragging = false;
        let mouseDragStartX = 0;
        let mouseDragStartScrollLeft = 0;

        const onMouseDown = (event) => {
          cancelScheduledShowcaseSnapToArtwork();
          if (isSwipeHinting) {
            endSwipeHint({ resetToStart: true, remember: true });
          } else {
            clearSwipeHintSchedulers();
          }
          if (event.button !== 0) return;
          event.preventDefault();
          isMouseDragging = true;
          mouseDragStartX = event.clientX;
          mouseDragStartScrollLeft = track.scrollLeft;
          draggedDistance = 0;
          track.classList.add('is-pointer-dragging');
          window.addEventListener('mousemove', onMouseMove);
        };

        const onMouseMove = (event) => {
          if (!isMouseDragging) return;
          event.preventDefault();
          const deltaX = event.clientX - mouseDragStartX;
          draggedDistance = Math.max(draggedDistance, Math.abs(deltaX));
          track.scrollLeft = mouseDragStartScrollLeft - deltaX;
        };
        const onShowcaseMouseMove = (event) => {
          if (!hoverCursor || !showcase.classList.contains('is-hovering')) return;
          const rect = showcase.getBoundingClientRect();
          hoverCursor.style.left = `${event.clientX - rect.left}px`;
          hoverCursor.style.top = `${event.clientY - rect.top}px`;
        };

        const stopMouseDrag = () => {
          if (!isMouseDragging) return;
          isMouseDragging = false;
          track.classList.remove('is-pointer-dragging');
          window.removeEventListener('mousemove', onMouseMove);
          const width = track.clientWidth || 1;
          const snappedIndex = Math.round(track.scrollLeft / width);
          track.scrollTo({ left: snappedIndex * width, behavior: 'smooth' });
        };

        const onTrackClickCapture = (event) => {
          if (draggedDistance > 6) {
            event.preventDefault();
            event.stopPropagation();
          }
        };

        const onResize = () => {
          if (mobileShowcaseQuery.matches) {
            clearSwipeHintSchedulers();
          } else {
            if (isSwipeHinting) endSwipeHint({ remember: false });
            else clearSwipeHintSchedulers();
          }
          syncFromScroll();
        };

        const onMouseEnterShowcase = (event) => {
          if (!hoverCursor) return;
          const rect = showcase.getBoundingClientRect();
          hoverCursor.style.left = `${event.clientX - rect.left}px`;
          hoverCursor.style.top = `${event.clientY - rect.top}px`;
          showcase.classList.add('is-hovering');
        };

        const onMouseLeaveShowcase = () => {
          showcase.classList.remove('is-hovering');
        };

        /* Auto-snap toward artwork queues two rAFs; swiping home first should win. Also ignore non-trusted/synthetic echoes. */
        let pendingArtworkSnapRafOuter = null;
        let pendingArtworkSnapRafInner = null;
        let heroSettleIdleTimer = null;
        let lastUserSettledHeroAt = -Infinity;
        let suppressHeroSettleUntil = 0;

        const cancelScheduledShowcaseSnapToArtwork = () => {
          if (pendingArtworkSnapRafOuter !== null) {
            cancelAnimationFrame(pendingArtworkSnapRafOuter);
            pendingArtworkSnapRafOuter = null;
          }
          if (pendingArtworkSnapRafInner !== null) {
            cancelAnimationFrame(pendingArtworkSnapRafInner);
            pendingArtworkSnapRafInner = null;
          }
        };

        const scheduleScrollShowcaseToArtwork = () => {
          if (!mobileShowcaseQuery.matches) return;
          cancelScheduledShowcaseSnapToArtwork();
          const scheduledAt = performance.now();
          pendingArtworkSnapRafOuter = requestAnimationFrame(() => {
            pendingArtworkSnapRafOuter = null;
            pendingArtworkSnapRafInner = requestAnimationFrame(() => {
              pendingArtworkSnapRafInner = null;
              if (!mobileShowcaseQuery.matches) return;
              /* User intentionally swiped to the hero slide after something queued auto-snap — do not overwrite. */
              if (lastUserSettledHeroAt > scheduledAt) return;
              const w = track.clientWidth || 1;
              if (pages.length < 2 || w < 16) return;
              if (isSwipeHinting) endSwipeHint({ resetToStart: false, remember: true });
              suppressHeroSettleUntil = performance.now() + 900;
              const behavior = reduceMotionQuery.matches ? 'auto' : 'smooth';
              track.scrollTo({ left: w, behavior });
              if (behavior === 'auto') setActiveIndex(1);
            });
          });
        };

        /* Standard variant options publish optionValueSelectionChange. Line-item Mount + colour/B&W fallback inputs are ignored there but still update the artwork viewer — scroll on their change (capture) too. */
        let unsubShowcaseOptionScroll = null;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          unsubShowcaseOptionScroll = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (msg) => {
            const sourceEvent = msg?.data?.event;
            const fromVariantLabelTap = Boolean(sourceEvent?.__hosFromVariantLabelTap);
            if (!sourceEvent?.isTrusted && !fromVariantLabelTap) return;
            const t = msg?.data?.event?.target;
            if (!t || !this.contains(t)) return;
            scheduleScrollShowcaseToArtwork();
          });
        }

        const onMountOrArtworkFallbackChange = (event) => {
          const fromVariantLabelTap = Boolean(event?.__hosFromVariantLabelTap);
          if (!event?.isTrusted && !fromVariantLabelTap) return;
          const t = event.target;
          if (!(t instanceof Element) || !this.contains(t)) return;
          if (
            !(
              t.matches('input[data-mount-option-input]') ||
              t.matches('input[data-artwork-bw-fallback-input]')
            )
          )
            return;
          scheduleScrollShowcaseToArtwork();
        };
        this.addEventListener('change', onMountOrArtworkFallbackChange, true);

        syncFromScroll();
        track.addEventListener('scroll', onScroll, { passive: true });
        track.addEventListener('touchstart', onTrackUserInterruptHint, { passive: true });
        track.addEventListener('click', onTrackClickCapture, true);
        track.addEventListener('mousedown', onMouseDown);
        showcase.addEventListener('mouseenter', onMouseEnterShowcase);
        showcase.addEventListener('mouseleave', onMouseLeaveShowcase);
        showcase.addEventListener('mousemove', onShowcaseMouseMove);
        window.addEventListener('mouseup', stopMouseDrag);
        window.addEventListener('resize', onResize);

        return () => {
          if (typeof unsubShowcaseOptionScroll === 'function') unsubShowcaseOptionScroll();
          this.removeEventListener('change', onMountOrArtworkFallbackChange, true);
          cancelScheduledShowcaseSnapToArtwork();
          if (heroSettleIdleTimer) {
            clearTimeout(heroSettleIdleTimer);
            heroSettleIdleTimer = null;
          }
          if (isSwipeHinting) {
            endSwipeHint({ remember: false });
          } else {
            clearSwipeHintSchedulers();
          }
          track.removeEventListener('scroll', onScroll);
          track.removeEventListener('touchstart', onTrackUserInterruptHint);
          track.removeEventListener('click', onTrackClickCapture, true);
          track.removeEventListener('mousedown', onMouseDown);
          showcase.removeEventListener('mouseenter', onMouseEnterShowcase);
          showcase.removeEventListener('mouseleave', onMouseLeaveShowcase);
          showcase.removeEventListener('mousemove', onShowcaseMouseMove);
          window.removeEventListener('mousemove', onMouseMove);
          window.removeEventListener('mouseup', stopMouseDrag);
          window.removeEventListener('resize', onResize);
        };
      }

      /**
       * Same horizontal “peek” nudge for the default media gallery when the hero+artwork showcase is not used.
       * Does not add custom mouse drag (Dawn/SliderComponent handles the list); only touch + scroll listeners.
       */
      initializeMobileProductGallerySliderHint() {
        if (this.querySelector('.product-media-showcase--has-hero-slide')) {
          return undefined;
        }
        const track = this.querySelector('media-gallery .product__media-list.slider--mobile');
        const overlay = track?.closest('slider-component');
        const slideCount = track
          ? track.querySelectorAll('.product__media-item, .grid__item.slider__slide, .slider__slide').length
          : 0;
        if (!track || !overlay || slideCount < 2) {
          return undefined;
        }

        const SWIPE_HINT_KEY = 'hos_pdp_product_swipe_v2';
        const mobileShowcaseQuery = window.matchMedia('(max-width: 749px)');
        const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const hintState = { timeout: null, raf: null };
        let isSwipeHinting = false;

        const markSwipeHintUsed = () => {
          try {
            sessionStorage.setItem(SWIPE_HINT_KEY, '1');
          } catch (e) {
            // ignore
          }
        };

        const clearSwipeHintSchedulers = () => {
          if (hintState.timeout) {
            clearTimeout(hintState.timeout);
            hintState.timeout = null;
          }
          if (hintState.raf) {
            cancelAnimationFrame(hintState.raf);
            hintState.raf = null;
          }
        };

        const endSwipeHint = (options = {}) => {
          const { resetToStart = true, remember = true } = options;
          isSwipeHinting = false;
          overlay.classList.remove('is-swipe-hint');
          track.classList.remove('swipe-hint--active-track');
          clearSwipeHintSchedulers();
          if (resetToStart) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          }
          if (remember) markSwipeHintUsed();
        };

        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        const runSwipeHintPeek = () => {
          if (reduceMotionQuery.matches || !mobileShowcaseQuery.matches) return;
          try {
            if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
          } catch (e) {
            // ignore
          }
          if (track.scrollLeft > 2) return;

          const w = track.clientWidth || 1;
          const maxPeek = Math.min(44, w * 0.11, w * 0.42);
          const tOut = 400;
          const tHold = 120;
          const tIn = 420;
          const tTotal = tOut + tHold + tIn;
          const t0 = performance.now();

          isSwipeHinting = true;
          overlay.classList.add('is-swipe-hint');
          track.classList.add('swipe-hint--active-track');

          const step = (now) => {
            if (!isSwipeHinting) return;
            if (!mobileShowcaseQuery.matches) {
              endSwipeHint({ remember: false });
              return;
            }
            const elapsed = now - t0;
            if (elapsed >= tTotal) {
              endSwipeHint({ resetToStart: true, remember: true });
              return;
            }
            let offset = 0;
            if (elapsed < tOut) {
              const u = easeInOutCubic(elapsed / tOut);
              offset = maxPeek * u;
            } else if (elapsed < tOut + tHold) {
              offset = maxPeek;
            } else {
              const u = (elapsed - tOut - tHold) / tIn;
              const v = 1 - easeInOutCubic(u);
              offset = maxPeek * v;
            }
            track.scrollLeft = offset;
            hintState.raf = requestAnimationFrame(step);
          };
          hintState.raf = requestAnimationFrame(step);
        };

        const onTrackUserInterrupt = () => {
          if (isSwipeHinting) {
            endSwipeHint({ resetToStart: true, remember: true });
          } else {
            clearSwipeHintSchedulers();
          }
        };

        const scheduleSwipeHint = () => {
          if (reduceMotionQuery.matches) return;
          try {
            if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
          } catch (e) {
            // ignore
          }
          clearSwipeHintSchedulers();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!mobileShowcaseQuery.matches) return;
              try {
                if (sessionStorage.getItem(SWIPE_HINT_KEY) === '1') return;
              } catch (e) {
                // ignore
              }
              hintState.timeout = setTimeout(() => {
                hintState.timeout = null;
                runSwipeHintPeek();
              }, 1000);
            });
          });
        };
        if (mobileShowcaseQuery.matches) {
          scheduleSwipeHint();
        }

        let scrollTicking = false;
        const onScroll = () => {
          if (scrollTicking) return;
          scrollTicking = true;
          requestAnimationFrame(() => {
            scrollTicking = false;
            if (!isSwipeHinting && track.scrollLeft > 6) {
              markSwipeHintUsed();
              clearSwipeHintSchedulers();
            }
          });
        };

        const onResize = () => {
          if (mobileShowcaseQuery.matches) {
            scheduleSwipeHint();
          } else if (isSwipeHinting) {
            endSwipeHint({ remember: false });
          } else {
            clearSwipeHintSchedulers();
          }
        };

        track.addEventListener('scroll', onScroll, { passive: true });
        track.addEventListener('touchstart', onTrackUserInterrupt, { passive: true });

        window.addEventListener('resize', onResize);

        return () => {
          if (isSwipeHinting) {
            endSwipeHint({ remember: false });
          } else {
            clearSwipeHintSchedulers();
          }
          track.removeEventListener('scroll', onScroll);
          track.removeEventListener('touchstart', onTrackUserInterrupt);
          window.removeEventListener('resize', onResize);
        };
      }

      initializeMobileScrollPill() {
        const pill = this.querySelector('.product-mobile-scroll-pill');
        if (!pill) return undefined;
        const originalPillParent = pill.parentNode;
        const originalPillNextSibling = pill.nextSibling;

        const mediaQuery = window.matchMedia('(max-width: 749px)');
        const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const heroBanner = this.querySelector('.product-hero-banner');
        const heroBannerCta = heroBanner?.querySelector('.product-hero-banner__cta');
        const getSubmitButton = () => {
          const buyActions = this.querySelector('.product-buy-actions');
          if (!buyActions) return null;
          return (
            buyActions.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
            buyActions.querySelector('.product-form__submit')
          );
        };
        const getVariantDetails = () => this.querySelector('.product-variant-details');
        const getOptionsToggleButton = () =>
          this.querySelector('.product-variant-details__summary') || this.querySelector('.product-variant-details summary');
        const getTargetContent = () => this.querySelector(`#ProductContent-${this.sectionId}`);
        const getVariantTarget = () =>
          this.querySelector('.product-variant-details, variant-selects, .product__info-container');
        let scrollAnimationFrame = null;
        let pillOffsetAnimationFrame = null;
        const movePillToPortal = () => {
          pill.classList.add('product-mobile-scroll-pill--portal');
          if (document.body && pill.parentNode !== document.body) {
            document.body.appendChild(pill);
          }
        };
        const restorePillFromPortal = () => {
          pill.classList.remove('product-mobile-scroll-pill--portal');
          if (pill.parentNode === document.body) {
            if (originalPillParent && originalPillParent.isConnected) {
              if (originalPillNextSibling && originalPillNextSibling.parentNode === originalPillParent) {
                originalPillParent.insertBefore(pill, originalPillNextSibling);
              } else {
                originalPillParent.appendChild(pill);
              }
            } else {
              pill.remove();
            }
          }
        };
        const syncPillPortalPlacement = () => {
          // Keep the floating pill portaled on all viewports so fixed positioning
          // is anchored to the viewport and never clipped by transformed/overflow ancestors.
          movePillToPortal();
        };

        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const animateScrollTo = (targetY) => {
          const startY = window.scrollY || window.pageYOffset || 0;
          const distance = targetY - startY;
          if (Math.abs(distance) < 1) return;

          if (reduceMotionQuery.matches) {
            window.scrollTo(0, targetY);
            return;
          }

          if (scrollAnimationFrame) cancelAnimationFrame(scrollAnimationFrame);

          const duration = 620;
          const startTime = performance.now();
          const step = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);
            window.scrollTo(0, startY + distance * easedProgress);

            if (progress < 1) {
              scrollAnimationFrame = requestAnimationFrame(step);
            } else {
              scrollAnimationFrame = null;
            }
          };

          scrollAnimationFrame = requestAnimationFrame(step);
        };

        const smoothScrollToTarget = () => {
          const contentTarget = getTargetContent();
          const mobileTarget = getVariantTarget();
          const isMobileViewport = mediaQuery.matches;
          const targetElement = isMobileViewport ? mobileTarget || contentTarget : contentTarget || mobileTarget;
          if (!targetElement) return;

          const targetTop = targetElement.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
          const headerHeight = isMobileViewport ? document.querySelector('.section-header')?.offsetHeight || 0 : 0;
          let scrollTopTarget = isMobileViewport
            ? Math.max(0, targetTop - headerHeight - 8)
            : Math.max(0, targetTop + 2);

          if (!isMobileViewport && contentTarget) {
            const contentTop = contentTarget.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
            const minTarget = isMobileViewport
              ? Math.max(0, contentTop - headerHeight - 8)
              : Math.max(0, contentTop + 2);
            const maxTarget = isMobileViewport ? minTarget : Math.max(minTarget, contentTop + 14);
            scrollTopTarget = Math.min(Math.max(scrollTopTarget, minTarget), maxTarget);
          }

          const currentScrollY = window.scrollY || window.pageYOffset || 0;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
          const documentHeight = Math.max(
            document.body?.scrollHeight || 0,
            document.documentElement?.scrollHeight || 0
          );
          const maxScrollableY = Math.max(0, documentHeight - viewportHeight);
          // Occasionally the computed target resolves to nearly the current position (layout race),
          // making pill taps appear to do nothing. Guarantee a small downward advance only in that case.
          // Preserve legitimate upward scrolling when the target is truly above the current position.
          if (Math.abs(scrollTopTarget - currentScrollY) <= 20) {
            const fallbackAdvance = isMobileViewport
              ? Math.round(viewportHeight * 0.55)
              : Math.round(viewportHeight * 0.45);
            scrollTopTarget = Math.min(maxScrollableY, currentScrollY + Math.max(180, fallbackAdvance));
          }

          animateScrollTo(scrollTopTarget);
        };
        const getBottomObstructionOffset = () => {
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
          const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
          if (!viewportHeight || !viewportWidth) return 0;

          const samplePoints = [0.2, 0.5, 0.8];
          const sampleY = Math.max(0, viewportHeight - 4);
          let maxOffset = 0;

          samplePoints.forEach((xPercent) => {
            const x = Math.round(viewportWidth * xPercent);
            const stack = document.elementsFromPoint(x, sampleY) || [];

            const blockingElement = stack.find((element) => {
              if (!element || element === pill || pill.contains(element)) return false;
              if (element === document.documentElement || element === document.body) return false;

              const style = window.getComputedStyle(element);
              if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
              if (style.position !== 'fixed' && style.position !== 'sticky') return false;

              const rect = element.getBoundingClientRect();
              if (rect.height < 24) return false;
              if (rect.width < viewportWidth * 0.4) return false;
              if (rect.top > viewportHeight) return false;
              if (rect.bottom < viewportHeight - 1) return false;
              if (rect.height > viewportHeight * 0.45) return false;

              return true;
            });

            if (!blockingElement) return;

            const rect = blockingElement.getBoundingClientRect();
            const overlap = Math.max(0, viewportHeight - rect.top);
            if (overlap > maxOffset) maxOffset = overlap;
          });

          // Account for mobile browser chrome (address/tab bars), which is not represented by DOM nodes.
          const visualViewport = window.visualViewport;
          if (visualViewport) {
            const occludedBottom = Math.max(
              0,
              viewportHeight - (visualViewport.height + visualViewport.offsetTop)
            );
            if (occludedBottom > maxOffset) maxOffset = occludedBottom;
          }

          return maxOffset > 0 ? maxOffset + 10 : 0;
        };
        const updatePillBottomOffset = () => {
          if (!mediaQuery.matches) {
            pill.style.removeProperty('--product-mobile-pill-bottom-offset');
            return;
          }

          const obstructionOffset = getBottomObstructionOffset();
          pill.style.setProperty('--product-mobile-pill-bottom-offset', `${obstructionOffset}px`);
        };
        const schedulePillBottomOffsetUpdate = () => {
          if (pillOffsetAnimationFrame) return;
          pillOffsetAnimationFrame = requestAnimationFrame(() => {
            pillOffsetAnimationFrame = null;
            updatePillBottomOffset();
          });
        };

        const updateVisibility = () => {
          const submitButton = getSubmitButton();
          const variantDetails = getVariantDetails();
          const optionsToggleButton = getOptionsToggleButton();
          const isMobileViewport = mediaQuery.matches;
          const submitButtonRect = submitButton?.getBoundingClientRect?.() || null;
          const optionsToggleRect = optionsToggleButton?.getBoundingClientRect?.() || null;
          const hasRenderableSubmitButton = Boolean(
            submitButton &&
              submitButtonRect &&
              submitButtonRect.height > 0 &&
              submitButton.getClientRects().length > 0
          );
          const hasRenderableOptionsToggle = Boolean(
            optionsToggleButton &&
              optionsToggleRect &&
              optionsToggleRect.height > 0 &&
              optionsToggleButton.getClientRects().length > 0
          );

          if (
            (isMobileViewport && !hasRenderableSubmitButton) ||
            (!isMobileViewport && !hasRenderableOptionsToggle && !hasRenderableSubmitButton)
          ) {
            pill.classList.remove('is-visible');
            heroBannerCta?.classList.remove('is-hidden-for-floating-pill');
            return;
          }

          const stickyHeaderOffset = document.querySelector('.section-header')?.offsetHeight || 0;
          const visibilityThreshold = stickyHeaderOffset + 8;
          const isOptionsOpen = Boolean(variantDetails?.hasAttribute('open'));
          const desktopTrigger = isOptionsOpen
            ? (hasRenderableSubmitButton ? submitButton : optionsToggleButton)
            : (hasRenderableOptionsToggle ? optionsToggleButton : submitButton);
          const desktopTriggerRect = desktopTrigger?.getBoundingClientRect?.() || null;
          const showPill = isMobileViewport
            ? Boolean(hasRenderableSubmitButton && submitButtonRect.bottom <= stickyHeaderOffset)
            : Boolean(desktopTriggerRect && desktopTriggerRect.bottom <= visibilityThreshold);
          pill.classList.toggle('is-visible', showPill);
          heroBannerCta?.classList.toggle('is-hidden-for-floating-pill', showPill);
          if (showPill) schedulePillBottomOffsetUpdate();
          if (!showPill && !isMobileViewport) pill.style.removeProperty('--product-mobile-pill-bottom-offset');
        };

        let scrollTicking = false;
        const onScroll = () => {
          if (scrollTicking) return;
          scrollTicking = true;
          requestAnimationFrame(() => {
            scrollTicking = false;
            updateVisibility();
          });
        };

        const onResize = () => {
          syncPillPortalPlacement();
          updateVisibility();
        };
        const onMediaQueryChange = () => {
          syncPillPortalPlacement();
          updateVisibility();
        };
        const onVariantDetailsToggle = () => updateVisibility();
        const onPillClick = (event) => {
          event.preventDefault();
          smoothScrollToTarget();
        };
        const onHeroBannerClick = (event) => {
          const trigger = event.target?.closest?.('.product-hero-banner__pill, .product-mobile-scroll-pill');
          if (!trigger) return;
          event.preventDefault();
          smoothScrollToTarget();
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        window.visualViewport?.addEventListener('resize', schedulePillBottomOffsetUpdate);
        window.visualViewport?.addEventListener('scroll', schedulePillBottomOffsetUpdate);
        mediaQuery.addEventListener?.('change', onMediaQueryChange);
        getVariantDetails()?.addEventListener('toggle', onVariantDetailsToggle);
        pill.addEventListener('click', onPillClick);
        heroBanner?.addEventListener('click', onHeroBannerClick);
        syncPillPortalPlacement();
        schedulePillBottomOffsetUpdate();

        updateVisibility();

        return () => {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onResize);
          window.visualViewport?.removeEventListener('resize', schedulePillBottomOffsetUpdate);
          window.visualViewport?.removeEventListener('scroll', schedulePillBottomOffsetUpdate);
          mediaQuery.removeEventListener?.('change', onMediaQueryChange);
          getVariantDetails()?.removeEventListener('toggle', onVariantDetailsToggle);
          pill.removeEventListener('click', onPillClick);
          heroBanner?.removeEventListener('click', onHeroBannerClick);
          heroBannerCta?.classList.remove('is-hidden-for-floating-pill');
          restorePillFromPortal();
          if (scrollAnimationFrame) cancelAnimationFrame(scrollAnimationFrame);
          if (pillOffsetAnimationFrame) cancelAnimationFrame(pillOffsetAnimationFrame);
        };
      }

      normalizePillOrientation(value) {
        if (value == null) return '';
        if (Array.isArray(value)) value = value.join(' ');
        const text = String(value).toLowerCase();
        if (!text) return '';
        if (text.includes('3:2') || text.includes('3 / 2') || text.includes('3-2')) return 'horizontal-3-2';
        if (text.includes('2:3') || text.includes('2 / 3') || text.includes('2-3')) return 'vertical-2-3';
        if (text.includes('horiz') || text.includes('landscape') || text.includes('horiztonal')) return 'horizontal';
        if (text.includes('square')) return 'square';
        if (text.includes('vert') || text.includes('portrait')) return 'vertical';
        return '';
      }

      initializeHeroPillFrameSync() {
        const layoutScript = this.querySelector(`#HeroPillLayout-${this.sectionId}`);
        if (!layoutScript) return undefined;
        let layout;
        try {
          layout = JSON.parse(layoutScript.textContent || '{}');
        } catch (e) {
          return undefined;
        }
        if (!layout?.layouts) return undefined;

        const update = (variant) => {
          if (!variant?.id) return;
          const layoutNode = this.querySelector(`#HeroPillLayout-${this.sectionId}`);
          let currentLayout = layout;
          if (layoutNode) {
            try {
              currentLayout = JSON.parse(layoutNode.textContent || '{}');
            } catch (e) {
              currentLayout = layout;
            }
          }
          if (!currentLayout?.layouts) return;

          const vs = this.querySelector(`#ArtworkViewerVariants-${this.sectionId}`);
          let variantRows = [];
          if (vs) {
            try {
              variantRows = JSON.parse(vs.textContent || '[]');
            } catch (e) {
              variantRows = [];
            }
          }

          const id = Number(variant.id);
          const row = variantRows.find((r) => Number(r.id) === id);
          const av = this.querySelector('artwork-viewer');
          const avImage = av?.querySelector?.('.product-artwork-viewer__image');

          let o = this.normalizePillOrientation(row?.artwork_orientation);
          if (!o && avImage) {
            const w = Number(avImage.naturalWidth || avImage.width);
            const h = Number(avImage.naturalHeight || avImage.height);
            if (w && h) o = w === h ? 'square' : w > h ? 'horizontal' : 'vertical';
          }
          // Prefer selected variant media geometry before any product-level defaults.
          if (!o && variant.featured_media?.preview_image) {
            const w = Number(variant.featured_media.preview_image.width);
            const h = Number(variant.featured_media.preview_image.height);
            if (w && h) {
              o = w === h ? 'square' : w > h ? 'horizontal' : 'vertical';
            }
          }
          if (!o && variant.featured_media?.aspect_ratio) {
            const ratio = Number(variant.featured_media.aspect_ratio);
            if (ratio) o = ratio >= 0.95 && ratio <= 1.05 ? 'square' : ratio > 1 ? 'horizontal' : 'vertical';
          }
          if (!o) o = this.normalizePillOrientation(av?.getAttribute('data-frame-orientation'));
          const familyOrientation = this.normalizePillOrientation(av?.dataset?.frameFamilyOrientation);
          if (
            familyOrientation === 'horizontal-3-2' ||
            familyOrientation === 'vertical-2-3'
          ) {
            // Frame-family orientation (from artwork metadata / size-chart) should win over square-cropped preview images.
            o = familyOrientation;
          } else if (!o) {
            o = familyOrientation;
          }
          if (!o) o = this.normalizePillOrientation(currentLayout?.defaultOrientation);
          if (!o) o = 'vertical';
          if (!['square', 'horizontal', 'vertical', 'horizontal-3-2', 'vertical-2-3'].includes(o)) o = 'vertical';

          const fallbackOrientation =
            o === 'horizontal-3-2'
              ? 'horizontal'
              : o === 'vertical-2-3'
                ? 'vertical'
                : 'vertical';
          const cfg =
            currentLayout.layouts[o] ||
            currentLayout.layouts[fallbackOrientation] ||
            currentLayout.layouts.vertical;
          this.querySelectorAll('.product-hero-banner__thumb').forEach((thumb) => {
            thumb.style.setProperty('--pill-frame-url', `url('${cfg.frameUrl}')`);
            thumb.style.setProperty('--pill-slot-w', cfg.slotW);
            thumb.style.setProperty('--pill-slot-h', cfg.slotH);
            thumb.style.setProperty('--pill-slot-x', cfg.slotX);
            thumb.style.setProperty('--pill-slot-y', cfg.slotY);
            thumb.style.setProperty('--pill-image-scale', cfg.scale);
            thumb.classList.toggle('product-hero-banner__thumb--square-orientation', o === 'square');
          });
        };

        const run = () => {
          let v = this.getSelectedVariant(this);
          if (!v) {
            const rawId = this.querySelector('input.product-variant-id, input[name="id"]')?.value;
            if (rawId) v = { id: Number(rawId) };
          }
          if (v) update(v);
        };

        run();

        const unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { sectionId, variant } = event.data || {};
          if (!variant || String(sectionId) !== String(this.sectionId)) return;
          update(variant);
        });

        return () => {
          if (unsub) unsub();
        };
      }

      resolveVariantTargetProductUrl(target) {
        if (!target) return '';
        if (target.dataset?.productUrl) return target.dataset.productUrl;
        if (target.tagName === 'SELECT' && target.selectedOptions?.[0]?.dataset?.productUrl) {
          return target.selectedOptions[0].dataset.productUrl;
        }
        if (target.tagName === 'OPTION') {
          return target.dataset?.productUrl || '';
        }
        if (target.tagName === 'LABEL') {
          const forId = target.getAttribute('for');
          const control =
            (forId ? document.getElementById(forId) : null) || target.querySelector('input, select, textarea');
          return this.resolveVariantTargetProductUrl(control);
        }
        return '';
      }

      variantSelectionFocusTargetId(target) {
        if (!target) return '';
        if (target.id) return target.id;
        if (target.tagName === 'OPTION') {
          const sel = target.closest('select');
          if (sel?.id) return sel.id;
        }
        if (target.tagName === 'LABEL') {
          const forId = target.getAttribute('for');
          if (forId) return forId;
          const inner = target.querySelector('input[id], select[id]');
          if (inner?.id) return inner.id;
        }
        return '';
      }

      focusAfterVariantSectionRender(target) {
        const id = this.variantSelectionFocusTargetId(target);
        if (id) document.getElementById(id)?.focus();
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.mobileOptionsSelectionLocked = false;
        this.resetProductFormState();

        const productUrl =
          this.resolveVariantTargetProductUrl(target) || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const variantSelectsRoot =
          target.closest?.('variant-selects') || event.target.closest?.('variant-selects');

        if (shouldFetchFullPage || !variantSelectsRoot) {
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: this.variantSelectionFocusTargetId(target),
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl),
          });
          return;
        }

        void (async () => {
          this.abortController?.abort();
          this.abortController = new AbortController();
          const { signal } = this.abortController;
          try {
            const requestUrl = await this.composeSectionAjaxUrl({
              productUrl,
              selectedOptionValues,
              variantSelectsRoot,
              signal,
            });

            fetch(requestUrl, { signal })
              .then((response) => response.text())
              .then((responseText) => {
                this.pendingRequestUrl = null;
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const cb = shouldSwapProduct
                  ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
                  : this.handleUpdateProductInfo(productUrl);
                cb(html);
              })
              .then(() => {
                this.focusAfterVariantSectionRender(target);
              })
              .catch((error) => {
                if (error.name === 'AbortError') console.log('Fetch aborted by user');
                else console.error(error);
              });
          } catch (error) {
            if (error.name === 'AbortError') return;
            console.error(error);
            this.renderProductInfo({
              requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
              targetId: this.variantSelectionFocusTargetId(target),
              callback: shouldSwapProduct
                ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
                : this.handleUpdateProductInfo(productUrl),
            });
          }
        })();
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const productInfoRoot = html.querySelector(selector) || html;
          const variant = this.getSelectedVariant(productInfoRoot);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (targetId) document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode || typeof productInfoNode.querySelector !== 'function') return null;
        const raw = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML?.trim() || '';
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch (e) {
          console.warn('product-info: could not parse data-selected-variant', e);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${this.stripUrlQuery(url)}?${params.join('&')}`;
      }

      stripUrlQuery(urlish) {
        const raw = (urlish || '').toString();
        return raw.split(/[?#]/)[0] || '';
      }

      pathnameFromProductLike(urlLike) {
        try {
          const raw = urlLike?.toString().trim() || this.dataset.url || '';
          const withOrigin =
            /^https?:\/\//i.test(raw) ? raw : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
          const u = new URL(withOrigin);
          return u.pathname.replace(/\/+$/, '') || '/';
        } catch {
          return '/';
        }
      }

      inferOptionSuffixPositionFromName(name) {
        if (!name) return null;
        const m = String(name).match(/-(\d+)$/);
        return m ? Number.parseInt(m[1], 10) : null;
      }

      normalizeOptionString(val) {
        return String(val ?? '').trim();
      }

      selectionsMatchVariant(selectionsMap, variant, optionCount) {
        for (let i = 1; i <= optionCount; i++) {
          const want = selectionsMap.get(i);
          const got = variant[`option${i}`];
          if (this.normalizeOptionString(want) !== this.normalizeOptionString(got)) return false;
        }
        return true;
      }

      /** For disabled option UI (e.g. frame colour under Print only), `selectionsMap` omits those positions. */
      selectionsMatchVariantPartial(selectionsMap, variant, optionCount) {
        for (let i = 1; i <= optionCount; i++) {
          if (!selectionsMap.has(i)) continue;
          const want = selectionsMap.get(i);
          const got = variant[`option${i}`];
          if (this.normalizeOptionString(want) !== this.normalizeOptionString(got)) return false;
        }
        return true;
      }

      /**
       * Frame swatches can stay checked+bdisabled for Print only — use that label to disambiguate partial matches.
       * @returns {{ position: number, value: string } | null}
       */
      readDisabledCheckedOptionInSwatchGroup(variantSelectsRoot) {
        const group = variantSelectsRoot?.querySelector('.product-form__input--frame-swatches');
        if (!group) return null;
        const input = group.querySelector('input[type="radio"]:checked[disabled]');
        if (!input) return null;
        const posRaw = input.getAttribute('data-option-position');
        const pos =
          posRaw != null && String(posRaw).trim() !== ''
            ? Number.parseInt(String(posRaw), 10)
            : Number.NaN;
        if (!Number.isFinite(pos) || pos < 1) return null;
        const value = String(input.value ?? '').trim();
        if (!value) return null;
        return { position: pos, value };
      }

      /** Fallback Color/B&W is not a Shopify option; Liquid always renders Color checked. Persist across AJAX section swaps. */
      readArtworkBwFallbackSelection(variantSelectsRoot) {
        if (!variantSelectsRoot) return null;
        const input = variantSelectsRoot.querySelector(
          '[data-artwork-bw-fallback] input[data-artwork-bw-fallback-input]:checked'
        );
        const v = input?.value;
        return v != null && String(v).trim() !== '' ? String(v) : null;
      }

      restoreArtworkBwFallbackSelection(variantSelectsRoot, previousValue) {
        if (!variantSelectsRoot || previousValue == null) return;

        const fieldset = variantSelectsRoot.querySelector('[data-artwork-bw-fallback]');
        if (!fieldset) return;

        const want = String(previousValue).trim();
        const radios = [...fieldset.querySelectorAll('input[data-artwork-bw-fallback-input]')];
        const match = radios.find((r) => String(r?.value ?? '').trim() === want);
        if (!match) return;

        match.checked = true;
        const selectedSpan = fieldset.querySelector('[data-selected-value]');
        if (selectedSpan) selectedSpan.textContent = match.value;
      }

      readMountLineItemSelection(variantSelectsRoot) {
        if (!variantSelectsRoot) return null;
        const input = variantSelectsRoot.querySelector(
          '.product-form__input--mount-line-item input[data-mount-option-input]:checked'
        );
        const v = input?.value;
        return v != null && String(v).trim() !== '' ? String(v) : null;
      }

      restoreMountLineItemSelection(variantSelectsRoot, previousValue) {
        if (!variantSelectsRoot || previousValue == null) return;
        const fieldset = variantSelectsRoot.querySelector('.product-form__input--mount-line-item');
        if (!fieldset) return;

        const want = String(previousValue).trim();
        const radios = [...fieldset.querySelectorAll('input[data-mount-option-input]')];
        const match = radios.find((r) => String(r?.value ?? '').trim() === want);
        if (!match) return;

        match.checked = true;
        const selectedSpan = fieldset.querySelector('[data-selected-value]');
        if (selectedSpan) selectedSpan.textContent = match.value;
      }

      collectSelectionsByOptionIndex(variantSelectsEl) {
        const map = new Map();
        if (!variantSelectsEl || typeof variantSelectsEl.querySelectorAll !== 'function') return map;

        variantSelectsEl.querySelectorAll('.product-form__input select').forEach((sel) => {
          if (sel.disabled) return;
          const opt = sel.selectedOptions?.[0];
          if (!opt) return;
          let pos = opt.getAttribute('data-option-position');
          let posNum =
            pos != null && String(pos).trim() !== '' ? Number.parseInt(String(pos), 10) : Number.NaN;
          if (!Number.isFinite(posNum)) {
            const fromName = this.inferOptionSuffixPositionFromName(sel.getAttribute('name'));
            posNum = fromName ?? Number.NaN;
          }
          if (!Number.isFinite(posNum) || posNum < 1) return;
          const val = opt.getAttribute('value');
          if (val == null) return;
          map.set(posNum, val);
        });

        variantSelectsEl.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach((input) => {
          if (input.disabled) return;
          let posRaw = input.getAttribute('data-option-position');
          let posNum =
            posRaw != null && String(posRaw).trim() !== ''
              ? Number.parseInt(String(posRaw), 10)
              : Number.NaN;
          if (!Number.isFinite(posNum)) {
            const fromName = this.inferOptionSuffixPositionFromName(input.getAttribute('name'));
            posNum = fromName ?? Number.NaN;
          }
          if (!Number.isFinite(posNum) || posNum < 1) return;
          map.set(posNum, String(input.value ?? ''));
        });

        return map;
      }

      async fetchCachedProductJs(pathname, signal) {
        if (!pathname || pathname === '/') return null;

        const key = pathname;
        this._productJsCache = this._productJsCache || new Map();
        if (this._productJsCache.has(key)) return this._productJsCache.get(key);

        const jsonUrl = `${pathname}.js`;
        const response = await fetch(jsonUrl, { signal, credentials: 'same-origin' });
        if (!response.ok) return null;
        const data = await response.json();
        this._productJsCache.set(key, data);
        return data;
      }

      async composeSectionAjaxUrl({ productUrl, selectedOptionValues, variantSelectsRoot, signal }) {
        const base = this.stripUrlQuery(productUrl);
        const params = [`section_id=${this.sectionId}`];
        const fallback = `${base}?section_id=${this.sectionId}${selectedOptionValues.length ? `&option_values=${selectedOptionValues.join(',')}` : ''}`;

        const pathname = this.pathnameFromProductLike(productUrl);
        const payload = await this.fetchCachedProductJs(pathname, signal);
        const optionCount = Number(payload?.options?.length ?? 0);
        if (!payload?.variants?.length || optionCount < 1) return fallback;

        const selections = this.collectSelectionsByOptionIndex(variantSelectsRoot);
        if (selections.size < 1) return fallback;

        let matched = payload.variants.filter((v) => this.selectionsMatchVariant(selections, v, optionCount));
        if (!matched.length) {
          matched = payload.variants.filter((v) => this.selectionsMatchVariantPartial(selections, v, optionCount));
        }
        if (!matched.length) return fallback;

        if (matched.length > 1) {
          const tie = this.readDisabledCheckedOptionInSwatchGroup(variantSelectsRoot);
          if (tie) {
            const key = `option${tie.position}`;
            const narrowed = matched.filter(
              (v) => this.normalizeOptionString(v[key]) === this.normalizeOptionString(tie.value)
            );
            if (narrowed.length) matched = narrowed;
          }
        }

        const variant = matched.find((v) => v.available) || matched[0];
        if (!variant?.id) return fallback;

        params.push(`variant=${variant.id}`);
        return `${base}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const currentSelectors = this.variantSelectors;
        if (!variantSelects || !currentSelectors) return;

        const currentAffirmContainers = Array.from(
          currentSelectors.querySelectorAll('.product-buy-actions__affirm')
        );
        const nextAffirmContainers = Array.from(
          variantSelects.querySelectorAll('.product-buy-actions__affirm')
        );
        if (currentAffirmContainers.length && nextAffirmContainers.length) {
          nextAffirmContainers.forEach((nextAffirmContainer, index) => {
            const currentAffirmContainer =
              currentAffirmContainers[index] ||
              currentAffirmContainers[currentAffirmContainers.length - 1];
            if (currentAffirmContainer) {
              const replacementNode =
                index < currentAffirmContainers.length
                  ? currentAffirmContainer
                  : currentAffirmContainer.cloneNode(true);
              nextAffirmContainer.replaceWith(replacementNode);
            }
          });
        }

        const wasVariantDetailsOpen = Boolean(
          currentSelectors.querySelector('.product-variant-details')?.hasAttribute('open')
        );

        const artworkBwFallbackSelection = this.readArtworkBwFallbackSelection(currentSelectors);
        const mountLineItemSelection = this.readMountLineItemSelection(currentSelectors);

        const postSwapCallbacks = [
          (newNode) => {
            if (wasVariantDetailsOpen) {
              newNode.querySelector('.product-variant-details')?.setAttribute('open', '');
            }
            this.restoreArtworkBwFallbackSelection(newNode, artworkBwFallbackSelection);
            this.restoreMountLineItemSelection(newNode, mountLineItemSelection);
          },
          ...this.postProcessHtmlCallbacks,
          () => {
            this.variantDetailsTransitionsCleanup?.();
            this.variantDetailsTransitionsCleanup = this.initializeVariantDetailsTransitions();

            this.mountPropertyCleanup?.();
            this.mountPropertyCleanup = this.initializeMountPropertySelector();

            this.artworkBwMediaCleanup?.();
            this.artworkBwMediaCleanup = this.initializeArtworkBwMedia();

            this.mobileScrollPillCleanup?.();
            this.mobileScrollPillCleanup = this.initializeMobileScrollPill();
          },
        ];

        const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
        if (isMobileViewport) {
          // Mobile: replace controls synchronously to avoid visible 500ms double-buffer lag while tapping options.
          this.preProcessHtmlCallbacks?.forEach((callback) => callback(variantSelects));
          const newNodeWrapper = document.createElement('div');
          HTMLUpdateUtility.setInnerHTML(newNodeWrapper, variantSelects.outerHTML);
          const newNode = newNodeWrapper.firstChild;
          currentSelectors.replaceWith(newNode);
          postSwapCallbacks.forEach((callback) => callback(newNode));
          return;
        }

        HTMLUpdateUtility.viewTransition(currentSelectors, variantSelects, this.preProcessHtmlCallbacks, postSwapCallbacks);
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const remoteProductInfo =
            html.getElementById(`MainProduct-${this.sectionId}`) ||
            html.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
            html.querySelector('product-info');
          if (remoteProductInfo?.dataset?.url) {
            this.dataset.url = remoteProductInfo.dataset.url;
          }

          const variant = this.getSelectedVariant(remoteProductInfo || html);
          const variantIdFromSection = remoteProductInfo?.querySelector?.(
            'input[name="id"].product-variant-id, input[name="id"]'
          )?.value;
          const resolvedVariantId = variant?.id || variantIdFromSection || null;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, resolvedVariantId);
          this.updateVariantInputs(resolvedVariantId);

          if (!variant && !resolvedVariantId) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.enforceMobileVariantDetailsExpanded();
          this.applyMobileAddToCartLock();
          this.updateAddToCartButtonLabelWithPrice();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.disabled = false;
          input.removeAttribute('disabled');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      shouldLockAddToCartOnMobile() {
        const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
        const hasVariantSelects = Boolean(this.querySelector('variant-selects'));
        if (!isMobileViewport || !hasVariantSelects) return false;

        // Only lock if we don't yet have a resolvable variant.
        // Many products have a preselected variant on load; locking in that case makes the button feel "dead" on Safari.
        const variantIdInput = this.querySelector('input[name="id"].product-variant-id, input[name="id"]');
        const hasVariantId = Boolean(variantIdInput?.value);
        return !hasVariantId;
      }

      applyMobileAddToCartLock() {
        const submitButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!submitButton) return;

        if (!this.mobileOptionsSelectionLocked) {
          if (this.mobileAddToCartLockAbortController) {
            this.mobileAddToCartLockAbortController.abort();
            this.mobileAddToCartLockAbortController = null;
          }
          if (submitButton.dataset.mobileAddToCartLocked === 'true') {
            submitButton.removeAttribute('aria-disabled');
            delete submitButton.dataset.mobileAddToCartLocked;
          }
          return;
        }

        // Keep the button tappable on mobile Safari; redirect taps to the options section instead.
        if (this.mobileAddToCartLockAbortController) {
          this.mobileAddToCartLockAbortController.abort();
        }
        this.mobileAddToCartLockAbortController = new AbortController();
        const { signal } = this.mobileAddToCartLockAbortController;

        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.removeAttribute('disabled');
        submitButton.dataset.mobileAddToCartLocked = 'true';
        this.updateAddToCartButtonLabelWithPrice();

        const intercept = (event) => {
          if (!this.mobileOptionsSelectionLocked) return;
          event.preventDefault();
          event.stopPropagation();
          this.scrollToMobileVariantTarget();
        };

        submitButton.addEventListener('click', intercept, { signal, capture: true });
        submitButton.addEventListener('touchstart', intercept, { signal, passive: false, capture: true });
      }

      getDisplayedPriceText() {
        const checkedSizePrice = this.querySelector(
          ".product-form__input--size-list input[type='radio']:checked + label.product-size-option .product-size-option__price"
        );
        if (checkedSizePrice?.textContent?.trim()) {
          return checkedSizePrice.textContent.replace(/\s+/g, ' ').trim();
        }

        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return '';

        const isVisible = (el) => {
          if (!el) return false;
          if (el.closest('[hidden], [aria-hidden="true"]')) return false;
          const style = window.getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
          return true;
        };

        const candidates = [
          ...priceRoot.querySelectorAll('.price-item--sale'),
          ...priceRoot.querySelectorAll('.price-item--regular'),
          ...priceRoot.querySelectorAll('.price-item'),
        ];

        const priceNode = candidates.find((node) => isVisible(node) && node.textContent?.trim());
        if (!priceNode) return '';

        return priceNode.textContent
          .replace(/\s+/g, ' ')
          .replace(/^(starting at|from)\s*:?\s*/i, '')
          .trim();
      }

      updateAddToCartButtonLabelWithPrice() {
        const submitButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const label =
          submitButton?.querySelector(':scope > span:not(.loading__spinner):not(.sold-out-message)') ||
          submitButton?.querySelector('span');
        if (!label) return;

        const baseText = window.variantStrings?.addToCart || 'Add to cart';
        const priceText = this.getDisplayedPriceText();
        label.textContent = priceText ? `${baseText}\u00A0\u00A0\u00A0${priceText}` : baseText;
      }

      enforceMobileVariantDetailsExpanded() {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const variantDetails = this.querySelector('.product-variant-details');
        if (!variantDetails) return;
        variantDetails.setAttribute('open', '');
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
